import { create } from 'zustand';

/**
 * Product announcement UI state, shared by the top-bar indicator and the
 * floating panel (they are siblings under `Layout`, so the open flag and the
 * read state cannot live in either one).
 *
 * Two distinct notions live here, and they must not be conflated:
 *
 * - **seen** (this store): a light, client-only read receipt. An announcement
 *   becomes seen once its slide has actually been rendered with the panel
 *   open. It only drives the unseen dot on the indicator, and it is persisted
 *   in `localStorage` — there is no backend endpoint for it.
 * - **dismissed** (backend, `useDismissProductAnnouncementAction`): permanent,
 *   removes the announcement from the feed for good.
 *
 * Storage key follows the repo convention established by
 * `use-unseen-recommendations-count.ts`: `fluenceur:<domain>:<state>:<brandId>`.
 * Read state is scoped per brand, and holds one entry per announcement id.
 */

const STORAGE_PREFIX = 'fluenceur:announcements:seen';

export const seenStorageKey = (brandId: string) => `${STORAGE_PREFIX}:${brandId}`;

function readSeenIds(brandId: string): string[] {
  if (!brandId || typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(seenStorageKey(brandId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    // Corrupted or unavailable storage degrades to "nothing seen yet": the dot
    // is a convenience indicator, never a correctness requirement.
    return [];
  }
}

function writeSeenIds(brandId: string, ids: string[]) {
  if (!brandId || typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(seenStorageKey(brandId), JSON.stringify(ids));
  } catch {
    // Ignore storage failures (quota, private mode).
  }
}

interface ProductAnnouncementState {
  open: boolean;
  /** Brand currently hydrated into `seenIds`. */
  brandId: string | null;
  seenIds: string[];
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /** Loads the persisted read state for a brand. Idempotent per brand. */
  hydrate: (brandId: string | null) => void;
  markSeen: (brandId: string, announcementId: string) => void;
}

export const useProductAnnouncementStore = create<ProductAnnouncementState>()((set, get) => ({
  // The panel keeps the current auto-surfacing behaviour: it is visible as
  // soon as there is at least one undismissed announcement. The indicator then
  // makes it re-openable for the rest of the session.
  open: true,
  brandId: null,
  seenIds: [],

  setOpen: (open) => set({ open }),

  toggle: () => set((state) => ({ open: !state.open })),

  hydrate: (brandId) => {
    if (get().brandId === brandId) return;
    // Read state only — the open flag is owned by the operator (via the
    // top-bar indicator) and must not be reset behind their back.
    set({ brandId, seenIds: brandId ? readSeenIds(brandId) : [] });
  },

  markSeen: (brandId, announcementId) => {
    const state = get();
    if (!brandId || state.brandId !== brandId || state.seenIds.includes(announcementId)) return;

    const seenIds = [...state.seenIds, announcementId];
    writeSeenIds(brandId, seenIds);
    set({ seenIds });
  },
}));
